import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Flag, CalendarDays, Sparkles, Trophy, Users, Medal, Star } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { NewsCard } from "@/components/NewsCard";
import { AthleteCard } from "@/components/AthleteCard";
import { EmptyState } from "@/components/EmptyState";
import { ParticipateButton } from "@/components/ParticipateButton";
import WelcomeDialog from "@/components/WelcomeDialog";
import { HighlightBlockVertical } from "@/components/HighlightBlockVertical";
import { HighlightBannerBottom } from "@/components/HighlightBannerBottom";
import { computeFeatureReason } from "@/lib/featureReason";

// V9.19 — Sprint 6 : HERO plein écran retiré (AthleteHero + HeroBig).
// Nouveau haut de page : "À la une" compact + 3 athlètes du moment.
const HERO_FALLBACK = "";

function HeroBig({ item }) {
  const img = item?.image_url || item?.cover_image_url || HERO_FALLBACK;
  const title = item?.title || "La mémoire vivante du sport polynésien commence ici.";
  const summary = item?.summary || "AITO.PF fédère athlètes, coachs, clubs et fédérations autour d'un média sportif, d'une vitrine premium et d'une mémoire numérique du Fenua.";
  const externalUrl = item?.url && /^https?:\/\//i.test(item.url) ? item.url : null;
  const internalSlug = item?.slug;
  const CTA = externalUrl ? (
    <a href={externalUrl} target="_blank" rel="noreferrer" data-testid="home-hero-primary-cta">
      <Button size="lg" className="bg-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon-2))] text-black font-medium">
        Lire l'article {item?.source_name ? `sur ${item.source_name}` : ""} <ArrowRight className="ml-1.5 h-4 w-4" />
      </Button>
    </a>
  ) : (
    <Link to={internalSlug ? `/actualites/${internalSlug}` : "/actualites"} data-testid="home-hero-primary-cta">
      <Button size="lg" className="bg-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon-2))] text-black font-medium">
        {item ? "Lire l'article" : "Découvrir les actualités"} <ArrowRight className="ml-1.5 h-4 w-4" />
      </Button>
    </Link>
  );
  return (
    <section className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pt-6">
      <div className="relative overflow-hidden rounded-2xl aito-grad-hero text-white shadow-[var(--shadow-hard)]">
        <div className="relative aspect-[16/10] sm:aspect-[16/8] lg:aspect-[16/7]">
          <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.05)_0%,rgba(0,0,0,0.55)_55%,rgba(0,0,0,0.9)_100%)]" />
          <div className="absolute left-0 right-0 bottom-0 p-5 sm:p-8 lg:p-10 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Badge className="bg-[hsl(var(--aito-gold))] text-black border-0">Pour les sportifs.</Badge>
              {item?.pinned && <Badge className="bg-[hsl(var(--aito-gold))] text-black border-0">★ Épinglé</Badge>}
              {!item?.pinned && item?.featured && <Badge className="bg-[hsl(var(--aito-lagoon))] text-black border-0">À la une</Badge>}
              <Badge variant="outline" className="border-white/30 text-white">La mémoire vivante du sport polynésien</Badge>
              {item?.category && <Badge variant="outline" className="border-white/30 text-white capitalize">{String(item.category).replace(/_/g, " ")}</Badge>}
            </div>
            <h1 data-testid="home-hero-headline" className="font-display text-2xl sm:text-4xl lg:text-5xl font-bold leading-tight max-w-3xl break-words [overflow-wrap:anywhere] line-clamp-3">
              {title}
            </h1>
            <p className="mt-3 max-w-2xl text-white/80 text-sm sm:text-base break-words [overflow-wrap:anywhere] line-clamp-3">{summary}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              {CTA}
              <Link to="/creer-ma-fiche" data-testid="home-hero-secondary-cta">
                <Button size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white/10">
                  Créer ma fiche
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// V11.8 — Widget Top 10 Athlètes pondéré (année en cours)
function TopAthletesWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/home/top-athletes", { params: { period: "year", limit: 10 } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);
  if (!loading && (!data || !data.items || data.items.length === 0)) return null;
  const items = data?.items || [];
  const podium = items.slice(0, 3);
  const rest = items.slice(3);
  return (
    <div data-testid="home-top-athletes-widget">
      <div className="flex items-center justify-between mb-3 bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm">
        <h2 className="font-display font-bold text-lg sm:text-xl flex items-center gap-2">
          <Star className="h-4 w-4 text-[hsl(var(--aito-gold))]" />
          Top 10 athlètes {data?.period_label && <span className="text-sm text-muted-foreground font-normal">· {data.period_label}</span>}
        </h2>
        <Link to="/athletes" className="text-xs text-[hsl(var(--aito-lagoon))] hover:underline font-semibold">Tous les athlètes</Link>
      </div>
      {loading ? (
        <div className="grid sm:grid-cols-3 gap-3"><Skeleton className="h-32" /><Skeleton className="h-32" /><Skeleton className="h-32" /></div>
      ) : (
        <>
          {/* Podium — 2 colonnes sur mobile pour éviter les cartes trop larges, 3 à partir de sm */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3" data-testid="top-athletes-podium">
            {podium.map((a, i) => {
              const rank = i + 1;
              const medalColor = rank === 1 ? "#D6A73A" : rank === 2 ? "#B9C2CC" : "#B07A4A";
              return (
                <Link key={a.id || i} to={`/athletes/${a.slug}`} className="block group" data-testid={`top-podium-${rank}`}>
                  <Card className="p-3 h-full hover:border-[hsl(var(--aito-lagoon))] transition relative bg-card shadow-sm">
                    <span className="absolute left-0 top-0 h-full w-1" style={{ backgroundColor: medalColor }} />
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        {a.photo_url ? (
                          <img src={a.photo_url} alt={a.full_name} className="h-12 w-12 rounded-full object-cover border-2" style={{ borderColor: medalColor }} />
                        ) : (
                          <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center border-2" style={{ borderColor: medalColor }}>
                            <Users className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                        <span className="absolute -bottom-1 -right-1 text-base" aria-hidden>
                          {rank === 1 ? "🥇" : rank === 2 ? "🥈" : "🥉"}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-bold text-sm truncate group-hover:text-[hsl(var(--aito-lagoon))]">{a.full_name}</div>
                        <div className="text-[10px] text-muted-foreground capitalize truncate">
                          {a.main_discipline || "—"}{a.island ? ` · ${a.island}` : ""}
                        </div>
                        <Badge variant="outline" className="text-[10px] mt-0.5">
                          {a.score} pts
                        </Badge>
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
          {/* Rangs 4-10 — 2 colonnes dès le mobile */}
          {rest.length > 0 && (
            <div className="grid grid-cols-2 gap-2" data-testid="top-athletes-rest">
              {rest.map((a, i) => (
                <Link key={a.id || i} to={`/athletes/${a.slug}`} className="flex items-center gap-2 p-2 rounded border bg-card hover:bg-secondary hover:border-[hsl(var(--aito-lagoon))] transition text-xs shadow-sm" data-testid={`top-rest-${i + 4}`}>
                  <span className="font-mono text-muted-foreground w-5 font-semibold">#{i + 4}</span>
                  {a.photo_url ? (
                    <img src={a.photo_url} alt={a.full_name} className="h-7 w-7 rounded-full object-cover" />
                  ) : (
                    <div className="h-7 w-7 rounded-full bg-muted" />
                  )}
                  <span className="flex-1 font-semibold truncate text-foreground">{a.full_name}</span>
                  <Badge variant="secondary" className="text-[9px] font-mono">{a.score} pts</Badge>
                </Link>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatRow({ icon: Icon, label, value, to }) {
  const inner = (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 hover:bg-secondary/80 transition-colors min-w-0">
      <Icon className="h-4 w-4 text-[hsl(var(--aito-lagoon))] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-mono text-xl font-semibold" data-testid={`home-stat-${label.toLowerCase()}`}>{value}</div>
        <div className="text-xs text-muted-foreground truncate">{label}</div>
      </div>
    </div>
  );
  if (to) {
    return <Link to={to} className="block" data-testid={`home-stat-link-${label.toLowerCase()}`}>{inner}</Link>;
  }
  return inner;
}

export default function HomePage() {
  const [news, setNews] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [stats, setStats] = useState({ athletes: 0, news: 0, federations: 0, competitions: 0 });
  const [loading, setLoading] = useState(true);
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    api.get("/site-settings").then((r) => setSiteSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [n, cal, a, fed, comp, upcomingComps, contribArts] = await Promise.all([
          api.get("/curator/homepage", { params: { limit: 10 } }),
          api.get("/calendar", { params: { upcoming_only: true, horizon_days: 21, limit: 50 } }).catch(() => ({ data: { items: [] } })),
          // V9.13 — remplacé /featured/top par /featured/now (scoring HERO déterministe, exclut le HERO)
          api.get("/athletes/featured/now", { params: { limit: 8 } }).catch(() => ({ data: { items: [] } })),
          api.get("/federations", { params: { limit: 1 } }),
          api.get("/competitions", { params: { limit: 1 } }),
          // V9.11 — Compteur dédié "Compétitions à venir" (events kind=competition).
          api.get("/calendar", { params: { upcoming_only: true, kind: "competition", limit: 1 } })
            .catch(() => ({ data: { count: 0 } })),
          // V9.13 — Articles contributeurs : on ne récupère QUE ceux flaggés pour la home
          // (pinned / featured / homepage_visible). Les autres restent dans /actualites uniquement.
          api.get("/contrib/articles/public", { params: { limit: 12, homepage_only: true } })
            .catch(() => ({ data: { items: [], count: 0 } })),
        ]);
        // V9.13 — Merge Curator + UserArticles avec priorité Curator si les deux sont pinned/featured.
        const curatorItems = n.data.items || [];
        const userArts = contribArts.data.items || [];

        const cPinned = curatorItems.filter((it) => it.pinned);
        const cFeatured = curatorItems.filter((it) => !it.pinned && it.featured);
        const cOther = curatorItems.filter((it) => !it.pinned && !it.featured);

        const uPinned = userArts.filter((it) => it.pinned);
        const uFeatured = userArts.filter((it) => !it.pinned && it.featured);
        const uOther = userArts.filter((it) => !it.pinned && !it.featured);

        const dateOf = (it) => new Date(
          it.published_at_source || it.published_at || it.date || it.created_at || 0
        ).getTime();
        const restMerged = [...cOther, ...uOther].sort((x, y) => dateOf(y) - dateOf(x));

        const merged = [
          ...cPinned, ...uPinned, ...cFeatured, ...uFeatured, ...restMerged,
        ];
        setNews(merged);
        const evts = (cal.data.items || [])
          .filter((e) => e.date || e.start_at)
          .sort((a, b) => new Date(a.date || a.start_at) - new Date(b.date || b.start_at));
        setCalendarEvents(evts);

        // V9.21 — Sprint UX Simplification : fallback robuste "Athlètes du moment".
        // Ne JAMAIS afficher "Aucun athlète" tant que des profils publics existent.
        // 1) /featured/now (scoring)  →  2) /athletes?limit=6 (publiés récents/populaires)
        let picks = a.data.items || [];
        if (picks.length < 3) {
          try {
            const extra = await api.get("/athletes", { params: { limit: 12, published: true } });
            const extraItems = extra.data.items || [];
            const seen = new Set(picks.map((p) => p.id));
            for (const item of extraItems) {
              if (picks.length >= 6) break;
              if (!seen.has(item.id)) {
                picks.push(item);
                seen.add(item.id);
              }
            }
          } catch { /* silencieux */ }
        }
        setAthletes(picks);
        setStats({
          athletes: picks.length,
          news: (n.data.count ?? 0) + (contribArts.data.count ?? 0),
          federations: fed.data.count ?? 0,
          competitions: upcomingComps.data.count ?? comp.data.count ?? 0,
        });
      } finally {
        setLoading(false);
      }
    })();
     
  }, []);

  const hero = news[0];
  const restNews = news.slice(1);

  // Countdown JP 2027 — piloté par site_settings (fallback 14 juillet 2027)
  const jpOpeningStr = siteSettings?.jp2027?.opening_date || "2027-07-14";
  const jpDays = Math.max(0, Math.ceil((new Date(`${jpOpeningStr}T00:00:00`) - new Date()) / (1000 * 60 * 60 * 24)));
  const jpLabel = siteSettings?.jp2027?.label || "Jeux du Pacifique 2027";
  const jpCta = siteSettings?.jp2027?.cta_text || "Suivez nos AITO en lice pour la délégation Polynésienne française";

  return (
    <div className="pb-12">
      {/* V12.x — Popup de bienvenue AITO.PF (première visite, dismissible). */}
      <WelcomeDialog />
      {/* V11.8 — Layout Accueil refondu : contenu principal à gauche,
          sidebar figée à droite (CTA + JP 2027 + Calendrier). La bannière
          globale (MainLayout) reste visible en filigrane. */}
      <section className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pt-6 grid lg:grid-cols-12 gap-6">
        {/* ============ COLONNE PRINCIPALE ============ */}
        <div className="lg:col-span-8 space-y-8 min-w-0">
          {/* Athlètes du moment — 3 cartes */}
          <div data-testid="home-athletes-of-moment-top">
            <div className="flex items-center justify-between mb-3 bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm">
              <h2 className="font-display font-bold text-lg sm:text-xl flex items-center gap-2">
                <Users className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
                Athlètes du moment
              </h2>
              <Link to="/athletes" className="text-xs text-[hsl(var(--aito-lagoon))] hover:underline font-semibold">Tous les athlètes</Link>
            </div>
            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {[0, 1, 2].map((i) => <Skeleton key={i} className="h-64 w-full rounded-lg" />)}
              </div>
            ) : athletes.length === 0 ? (
              <EmptyState
                icon={Users}
                title="Les premiers athlètes arrivent."
                description="Sois l'un des premiers à publier ta fiche sur AITO.PF."
                action={<Link to="/creer-ma-fiche"><Button className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">Créer ma fiche</Button></Link>}
              />
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {athletes.slice(0, 3).map((a) => (
                  <AthleteCard
                    key={a.id}
                    athlete={a}
                    featureReason={a.feature_reason || computeFeatureReason(a)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* V11.8 — Top 10 pondéré (année en cours) */}
          <TopAthletesWidget />

          {/* À la une compact */}
          <div>
            <div className="flex items-center justify-between mb-3 bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm">
              <h2 className="font-display font-bold text-lg sm:text-xl flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
                À la une
              </h2>
              <Link to="/actualites" className="text-xs text-[hsl(var(--aito-lagoon))] hover:underline font-semibold">Toutes les actualités</Link>
            </div>
            {loading ? (
              <Skeleton className="h-32 w-full rounded-lg" />
            ) : hero ? (
              <div data-testid="home-featured-compact">
                <NewsCard item={hero} compact />
              </div>
            ) : (
              <EmptyState
                icon={Sparkles}
                title="Bientôt en une."
                description="Les premières actualités arrivent avec vos contributions."
              />
            )}
          </div>

          {/* Actualités récentes */}
          <div>
            <div className="flex items-center justify-between mb-4 bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm">
              <h2 className="font-display font-bold text-xl sm:text-2xl">Actualités récentes</h2>
              <Link to="/actualites" className="text-sm text-[hsl(var(--aito-lagoon))] hover:underline font-semibold">Toutes les actualités</Link>
            </div>
            {restNews.length === 0 && !loading ? (
              <EmptyState
                icon={Sparkles}
                title="Le fil démarre bientôt."
                description="Les premières actualités arriveront dès que les sportifs, clubs et fédérations alimentent la plateforme."
                action={<Link to="/creer-ma-fiche"><Button className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">Publier la première actu</Button></Link>}
              />
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {restNews.slice(0, 6).map((n) => <NewsCard key={n.id} item={n} />)}
              </div>
            )}
          </div>
        </div>

        {/* ============ SIDEBAR DROITE — STICKY ============ */}
        <aside className="lg:col-span-4" data-testid="home-sticky-sidebar">
          <div className="lg:sticky lg:top-20 space-y-4">
            {/* Bloc CTA fixé : Découvrir / Gérer / Soutenir */}
            <Card
              className="p-4 border-[hsl(var(--aito-lagoon))]/30 bg-background/95 backdrop-blur-sm shadow-md"
              data-testid="home-cta-block"
            >
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
                <h3 className="font-display font-semibold text-sm">Rejoignez AITO.PF</h3>
              </div>
              <div className="space-y-2">
                <Link to="/athletes" data-testid="home-cta-athletes" className="block">
                  <Button size="sm" className="w-full justify-start bg-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon-2))] text-black font-medium">
                    <Users className="h-4 w-4 mr-2" /> Découvrir les athlètes
                  </Button>
                </Link>
                <Link to="/creer-ma-fiche" data-testid="home-cta-create" className="block">
                  <Button size="sm" variant="outline" className="w-full justify-start border-[hsl(var(--aito-gold))] text-[hsl(var(--aito-gold))] hover:bg-[hsl(var(--aito-gold))] hover:text-black font-medium">
                    <Sparkles className="h-4 w-4 mr-2" /> Créer / gérer ma fiche
                  </Button>
                </Link>
                <Link to="/soutien" data-testid="home-cta-support" className="block">
                  <Button size="sm" variant="ghost" className="w-full justify-start hover:bg-muted font-medium">
                    <Trophy className="h-4 w-4 mr-2" /> Soutenir un sportif
                  </Button>
                </Link>
              </div>
            </Card>

            {/* Bloc JP 2027 fixé */}
            <Card
              className="p-4 border-[hsl(var(--aito-gold))]/30 bg-gradient-to-br from-[hsl(var(--aito-gold))]/10 to-background/95 backdrop-blur-sm shadow-md"
              data-testid="home-jp2027-widget"
            >
              <div className="flex items-center gap-2 mb-2">
                <Flag className="h-4 w-4 text-[hsl(var(--aito-gold))]" />
                <h3 className="font-display font-semibold text-sm">{jpLabel}</h3>
              </div>
              <div className="flex items-baseline gap-2" data-testid="home-jp2027-countdown">
                <span className="font-mono text-2xl font-bold text-[hsl(var(--aito-gold))]">J-{jpDays}</span>
                <span className="text-[11px] text-muted-foreground">avant les Jeux</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">{jpCta}</p>
              <Link to="/road-2027" data-testid="home-road-2027-cta">
                <Button size="sm" variant="outline" className="mt-3 border-[hsl(var(--aito-gold))] text-[hsl(var(--aito-gold))] hover:bg-[hsl(var(--aito-gold))] hover:text-black w-full">
                  Découvrir nos AITO en lice
                </Button>
              </Link>
            </Card>

            {/* V13 — Zone 1 : Bloc vertical de mise en avant (sous JP 2027) */}
            <HighlightBlockVertical />

            {/* Calendrier — reste dans la sidebar mais après les 2 blocs figés */}
            <Card data-testid="home-calendar-module" className="p-4 bg-background/95 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
                  <h3 className="font-display font-semibold text-sm">Prochains rendez-vous</h3>
                </div>
                <Link to="/calendrier" className="text-xs text-[hsl(var(--aito-lagoon))] hover:underline">Tout le calendrier</Link>
              </div>
              <p className="text-[11px] text-muted-foreground mb-3">3 prochaines semaines</p>
              {calendarEvents.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">Aucun événement programmé pour les 3 prochaines semaines.</p>
              ) : (
                <ul className="space-y-2.5">
                  {calendarEvents.slice(0, 4).map((e) => {
                    const d = new Date(e.date || e.start_at);
                    const dayLabel = d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
                    return (
                      <li key={e.id} className="flex flex-col gap-1.5 min-w-0" data-testid="home-calendar-event">
                        <div className="flex items-start gap-2 text-sm min-w-0">
                          <Badge variant="outline" className="text-[10px] mt-0.5 shrink-0 font-mono">{dayLabel}</Badge>
                          <div className="flex-1 min-w-0">
                            <div className="truncate font-medium leading-tight">{e.title}</div>
                            <div className="text-[10px] text-muted-foreground capitalize truncate">
                              {[e.discipline, e.commune || e.island || e.location].filter(Boolean).join(" · ")}
                            </div>
                          </div>
                        </div>
                        <div className="pl-12">
                          <ParticipateButton eventId={e.id} eventTitle={e.title} size="sm" />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </div>
        </aside>
      </section>

      {/* V13 — Zone 2 : Bande basse discrète */}
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <HighlightBannerBottom />
      </div>
    </div>
  );
}
